// src/pages/marketplace/tenant/TenantProductsPage.tsx
// Tenant's subscribed products overview with card grid layout.

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useProduct, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
  Badge,
  ErrorAlert,
} from '@react/components';
import { StatusBadge } from '@/components/StatusBadge';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Map ProductTenantStatus to StatusBadge variant. */
function statusVariant(status: string): string {
  switch (status) {
    case 'PRODUCT_TENANT_STATUS_ACTIVE':
      return 'success';
    case 'PRODUCT_TENANT_STATUS_SUSPENDED':
      return 'error';
    case 'PRODUCT_TENANT_STATUS_PENDING_SETUP':
      return 'info';
    case 'PRODUCT_TENANT_STATUS_CANCELLED':
      return 'secondary';
    default:
      return 'warning';
  }
}

/** Human-readable status label. */
function statusLabel(status: string, t: (k: string) => string): string {
  switch (status) {
    case 'PRODUCT_TENANT_STATUS_ACTIVE':
      return t('marketplace.status.active');
    case 'PRODUCT_TENANT_STATUS_SUSPENDED':
      return t('marketplace.status.suspended');
    case 'PRODUCT_TENANT_STATUS_PENDING_SETUP':
      return t('marketplace.status.pendingSetup');
    case 'PRODUCT_TENANT_STATUS_CANCELLED':
      return t('marketplace.status.cancelled');
    default:
      return status;
  }
}

/** Calculate remaining trial days from an ISO timestamp. */
function trialDaysLeft(trialEndsAt: string | undefined): number | null {
  if (!trialEndsAt) return null;
  const end = new Date(trialEndsAt).getTime();
  const now = Date.now();
  const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 0;
}

/** Percentage of usage (clamped to 0-100). */
function usagePercent(current: number, limit: number): number {
  if (limit <= 0) return 0;
  return Math.min(Math.round((current / limit) * 100), 100);
}

/** Color class for usage bar. */
function usageColor(percent: number): string {
  if (percent >= 90) return 'bg-red-500';
  if (percent >= 70) return 'bg-yellow-500';
  return 'bg-green-500';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * TenantProductsPage - lists products the current tenant is subscribed to.
 * Route: /settings/products
 */
export const TenantProductsPage = observer(() => {
  const store = useProduct();
  const { t } = useI18n();

  useEffect(() => {
    store.ListMyProductProfiles();
  }, []);

  return (
    <div className="space-y-6">
      {/* Page header. The tab already names this page; the heading stays for screen readers. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="sr-only">{t('marketplace.tenant.title')}</h1>
        <Link to="/marketplace" className="sm:ml-auto">
          <Button>{t('marketplace.browse')}</Button>
        </Link>
      </div>

      {/* Error */}
      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Loading */}
      {store.isLoading && (
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">{t('common.loading')}</div>
        </div>
      )}

      {/* Empty state */}
      {!store.isLoading && store.items.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-muted-foreground"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m8.25 3v6.75m0 0l-3-3m3 3l3-3M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-1">
              {t('marketplace.tenant.empty')}
            </h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm">
              {t('marketplace.tenant.emptyDescription')}
            </p>
            <Link to="/marketplace">
              <Button>{t('marketplace.browse')}</Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Product card grid */}
      {!store.isLoading && store.items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {store.items.map((item: any) => {
            const productId = item.product?.entity?.id || item.product?.id || item.entity?.id;
            const product = item.product ?? item;
            const productName = product.name ?? '';
            const tagline = product.tagline ?? '';
            const iconUrl = product.icon_url;
            const colorPrimary = product.color_primary || '#6366f1';
            const webUrl = product.web_url;
            const status = item.status ?? 'PRODUCT_TENANT_STATUS_ACTIVE';
            const isTrial = item.is_trial;
            const trialEndsAt = item.trial_ends_at;
            const usageLimits: Record<string, number> = item.usage_limits ?? {};
            const currentUsage: Record<string, number> = item.current_usage ?? {};

            const daysLeft = trialDaysLeft(trialEndsAt);

            return (
              <Card key={productId} className="flex flex-col">
                <CardHeader className="pb-3">
                  <div className="flex items-start gap-3">
                    {/* Product icon */}
                    {iconUrl ? (
                      <img
                        src={iconUrl}
                        alt={productName}
                        className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                      />
                    ) : (
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 text-white font-bold text-sm"
                        style={{ backgroundColor: colorPrimary }}
                      >
                        {productName.charAt(0).toUpperCase()}
                      </div>
                    )}

                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-base truncate">
                        {productName}
                      </CardTitle>
                      {tagline && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          {tagline}
                        </p>
                      )}
                    </div>

                    {/* Status badge */}
                    <StatusBadge
                      variant={statusVariant(status)}
                      label={statusLabel(status, t)}
                      size="sm"
                    />
                  </div>
                </CardHeader>

                <CardContent className="flex-1 space-y-4 pb-3">
                  {/* Trial countdown */}
                  {isTrial && daysLeft !== null && (
                    <div className="flex items-center gap-2">
                      <Badge variant={daysLeft <= 7 ? 'warning' : 'default'} size="sm">
                        {daysLeft > 0
                          ? t('marketplace.tenant.trialDays', { count: daysLeft })
                          : t('marketplace.tenant.trialExpired')}
                      </Badge>
                    </div>
                  )}

                  {/* Usage bars */}
                  {Object.keys(usageLimits).length > 0 && (
                    <div className="space-y-2">
                      {Object.entries(usageLimits).map(([key, limit]) => {
                        const current = currentUsage[key] ?? 0;
                        const percent = usagePercent(current, limit as number);
                        return (
                          <div key={key}>
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="text-muted-foreground capitalize">
                                {key.replace(/_/g, ' ')}
                              </span>
                              <span className="text-foreground font-medium">
                                {current} / {limit}
                              </span>
                            </div>
                            <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${usageColor(percent)}`}
                                style={{ width: `${percent}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>

                <CardFooter className="flex items-center gap-2 pt-3 border-t border-border">
                  <Link to={`/settings/products/${productId}`} className="flex-1">
                    <Button variant="secondary" size="sm" className="w-full">
                      {t('marketplace.tenant.manage')}
                    </Button>
                  </Link>
                  {webUrl && (
                    <a href={webUrl} target="_blank" rel="noopener noreferrer">
                      <Button variant="ghost" size="sm">
                        {t('marketplace.tenant.open')}
                      </Button>
                    </a>
                  )}
                </CardFooter>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
});

export default TenantProductsPage;
